import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle, Clock, RefreshCw, XCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Badge } from '~/components/ui/badge';
import { Button } from '~/components/ui/button';
import { api } from '~/lib/api';

type Step = {
  step: string;
  status: string;
  errorMessage: string | null;
  completedAt: string | null;
};

function StepBadge({ status }: { status: string }) {
  const { t } = useTranslation();
  if (status === 'completed')
    return (
      <Badge variant="outline" className="gap-1 text-green-600 border-green-200">
        <CheckCircle className="h-3 w-3" />
        {t('atsPush.status.completed')}
      </Badge>
    );
  if (status === 'failed')
    return (
      <Badge variant="destructive" className="gap-1">
        <XCircle className="h-3 w-3" />
        {t('atsPush.status.failed')}
      </Badge>
    );
  return (
    <Badge variant="secondary" className="gap-1">
      <Clock className="h-3 w-3" />
      {t('atsPush.status.pending')}
    </Badge>
  );
}

interface AtsPushStatusProps {
  orgId: string;
  deptId: string;
  jobId: string;
  applicationId: string;
  isAdmin: boolean;
  scoringCompleted: boolean;
  variant?: 'card' | 'inline';
}

export function AtsPushStatus({
  orgId,
  deptId,
  jobId,
  applicationId,
  isAdmin,
  scoringCompleted,
  variant = 'card'
}: AtsPushStatusProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const invalidate = (): void => {
    void queryClient.invalidateQueries({
      queryKey: ['ats-push-status', orgId, deptId, jobId, applicationId]
    });
  };

  const { data, isLoading } = useQuery({
    queryKey: ['ats-push-status', orgId, deptId, jobId, applicationId],
    queryFn: async (): Promise<{ steps: Step[] }> => {
      const res = await api.api.org[':orgId'].department[':deptId'].jobs[':jobId'].applications[
        ':applicationId'
      ]['ats-push-status'].$get({
        param: { orgId, deptId, jobId, applicationId }
      });
      if (!res.ok) throw new Error('Failed to fetch ATS push status');
      return res.json();
    },
    refetchInterval: (query) => {
      const steps = query.state.data?.steps ?? [];
      if (steps.some((s) => s.status === 'pending')) return 5000;
      return false;
    }
  });

  const retry = useMutation({
    mutationFn: async () => {
      const res = await api.api.org[':orgId'].department[':deptId'].jobs[':jobId'].applications[
        ':applicationId'
      ]['retry-ats-push'].$post({
        param: { orgId, deptId, jobId, applicationId }
      });
      if (!res.ok) throw new Error('Retry failed');
    },
    onSuccess: invalidate
  });

  const resetStep = useMutation({
    mutationFn: async (step: string) => {
      const res = await api.api.org[':orgId'].department[':deptId'].jobs[':jobId'].applications[
        ':applicationId'
      ]['reset-ats-step'].$post({
        param: { orgId, deptId, jobId, applicationId },
        json: { step: step as 'transcript' | 'stage_move' }
      });
      if (!res.ok) throw new Error('Reset failed');
    },
    onSuccess: invalidate
  });

  if (isLoading) return null;
  if (!scoringCompleted && !data?.steps.length) return null;

  const steps = data?.steps ?? [];
  const hasFailed = steps.some((s) => s.status === 'failed');
  const noSteps = steps.length === 0;

  const content = (
    <div className="space-y-3">
      <p className="text-sm font-medium text-foreground">{t('atsPush.title')}</p>
      <div className="space-y-2">
        {steps.map((step) => (
          <div key={step.step} className="space-y-1">
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm text-muted-foreground">
                {step.step === 'stage_move'
                  ? t('atsPush.steps.stage_move')
                  : t('atsPush.steps.transcript')}
              </span>
              <div className="flex items-center gap-1.5">
                <StepBadge status={step.status} />
                {isAdmin && step.status === 'completed' && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-5 w-5 p-0 text-muted-foreground hover:text-foreground"
                    title={t('atsPush.rerunStep')}
                    disabled={resetStep.isPending}
                    onClick={() => {
                      if (window.confirm(t('atsPush.rerunConfirm'))) {
                        resetStep.mutate(step.step);
                      }
                    }}
                  >
                    <RefreshCw className="h-3 w-3" />
                  </Button>
                )}
              </div>
            </div>
            {step.errorMessage && (
              <p className="text-xs text-destructive break-words">{step.errorMessage}</p>
            )}
          </div>
        ))}
      </div>
      {isAdmin && (hasFailed || noSteps) && (
        <Button
          size="sm"
          variant="outline"
          onClick={() => {
            retry.mutate();
          }}
          disabled={retry.isPending}
          className="gap-1"
        >
          <RefreshCw className="h-3 w-3" />
          {retry.isPending ? t('atsPush.retrying') : t('atsPush.retry')}
        </Button>
      )}
    </div>
  );

  if (variant === 'inline') return content;

  return <div className="rounded-lg border p-4 max-w-sm">{content}</div>;
}
